import { datadogRum } from "@datadog/browser-rum"
import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit"
import { getUserRole } from "api/collections"
import jwtDecode from "jwt-decode"
import { delayedPromise, exponentialBackingOff } from "utils"
import lock from "utils/lock/lock"

let backingOff = exponentialBackingOff(1000, 1, 2, 16000)
let lastLoginAttempt = null

const refreshLock = lock()

const refreshToken = createAsyncThunk(
  "user/refreshToken",
  async ({ auth }, thunkAPI) => {
    const lockRef = refreshLock.acquire()
    if (!lockRef) throw Error("Cannot acquire lock.")
    refreshLock.release(lockRef)
    try {
      return {
        token: auth.token,
        tokenExpires: auth.tokenParsed.exp
      }
    } catch (e) {
      console.error(e)
      thunkAPI.dispatch(userLogout)
    }
  }
)
const userLogout = createAsyncThunk(
  "user/userLogout",
  async ({ auth }, thunkAPI) => {
    thunkAPI.dispatch(actions.isLoggingOut(true))
    await auth.oktaAuth.signOut()
    return null
  }
)

const userLogin = createAsyncThunk(
  "user/userLogin",
  async ({ auth, extension }, thunkAPI) => {
    const delay = backingOff.next().value
    const now = new Date()
    if (!lastLoginAttempt) {
      lastLoginAttempt = now
    } else if (now - lastLoginAttempt < delay) {
      await delayedPromise(delay - (now - lastLoginAttempt)).promise
      lastLoginAttempt = new Date()
    }
    thunkAPI.dispatch(actions.isLoggingIn(true))
    const authState = auth
    const claims = authState.idTokenParsed.email
    const user = claims.split("@")[0]
    const host = extension?.config?.env?.clientApiGateway?.URL
    if (host === undefined) throw new Error("No Client API URL defined.")

    const token = authState.token
    const decoded = jwtDecode(token)
    const tokenExpires = decoded.exp
    const requestAuth = { email: claims, token }

    const roleAndPolicy = (await getUserRole({ auth: requestAuth, host }, user)) // { "user": "chliu@deloitte.com", "role": "Portal-Admin", "portal_policy": { "modules": [{ "module": "service", "paths": ["/", "/requests"] }, { "module": "security", "paths": ["/"] }, { "module": "infrastructure", "paths": ["/"] }, { "module": "", "paths": ["/"] }, { "module": "cloud-financials", "paths": ["/"] }, { "module": "infra-monitoring", "paths": ["/"] }, { "module": "self-service", "paths": ["/"] }, { "module": "key-contacts", "paths": ["/"] }, { "module": "quick-links", "paths": ["/"] }] } };
      .data

    const datadogUser = {
      name: auth.idTokenParsed.name || "Undefined",
      email: auth.idTokenParsed.email || "Undefined",
      role: roleAndPolicy.role,
      domain: auth.idTokenParsed.email?.split("@")[1] || "Undefined"
    }
    datadogRum.setUser(datadogUser)
    const payload = {
      name: user,
      email: claims,
      token,
      tokenExpires,
      role: roleAndPolicy.role,
      policy: roleAndPolicy.portal_policy
      // policy: {
      //   modules: [
      //     {
      //       module: "admin",
      //       paths: [
      //         "/",
      //         "/application-monitoring",
      //         "/maintenance",
      //         "/uar-intervals"
      //       ]
      //     },
      //     { module: "cms", paths: ["/"] },
      //     { module: "canned-reports", paths: ["/"] },
      //     { module: "pod-datadog-compliance", paths: ["/"] },
      //     { module: "pod-vm-inventory", paths: ["/"] },
      //     { module: "pod-user-access", paths: ["/"] },
      //     { module: "pod-zero-day-vulnerabilities", paths: ["/"] },
      //     { module: "pod-health-assessment", paths: ["/"] },
      //     { module: "", paths: ["/"] },
      //     { module: "service", paths: ["/"] },
      //     { module: "security", paths: ["/"] },
      //     { module: "infra-monitoring", paths: ["/"] },
      //     { module: "vm-inventory", paths: ["/"] },
      //     {
      //       module: "resources",
      //       paths: ["/", "/quick-links", "/key-contacts", "/cloud-alerts"]
      //     },
      //     {
      //       module: "secrets",
      //       paths: ["/"]
      //     },
      //     {
      //       module: "cms-users",
      //       paths: [
      //         "/",
      //         "/vm-backup",
      //         "/cloud-financials",
      //         "/key-rotation",
      //         "/prisma-cloud",
      //         "/fixed-vulnerabilities",
      //         "/user-access",
      //         "/monitoring-compliance",
      //         "/zero-day-vulnerabilities"
      //       ]
      //     },
      //     { module: "release-notes", paths: ["/"] },
      //     { module: "user-review", paths: ["/"] },
      //     { module: "training-videos", paths: ["/"] },
      //     { module: "dashboards", paths: ["/"] }
      //   ]
      // }
    }
    backingOff = exponentialBackingOff(1000, 1, 2, 16000)
    return payload
  }
)

const createUserSlice = (
  initialState = {
    projectsFetching: false,
    projects: null,
    projectNames: null,
    accountsFetching: false,
    accounts: null,
    allowedPortalComponents: {},
    role: null,
    policy: null,
    name: null,
    email: null,
    isAuthenticated: false,
    tokenRefreshing: false,
    bearerToken: null,
    isLoggingIn: null,
    isLoggingOut: null,
    tokenExpires: null,
    clientView: false
  }
) =>
  createSlice({
    name: "user",
    initialState,
    reducers: {
      isLoggingOut(state, action) {
        const isLoggingOut = action.payload
        state.isLoggingOut = isLoggingOut
      },
      isLoggingIn(state, action) {
        const isLoggingIn = action.payload
        state.isLoggingIn = isLoggingIn
      },
      userEmail(state, action) {
        const email = action.payload
        state.email = email
      },
      userIsAuthenticated(state, action) {
        const isAuthenticated = action.payload
        state.isAuthenticated = isAuthenticated
      },
      userLogout(state) {
        state.name = null
        state.email = null
        state.projects = []
        state.isAuthenticated = false
        state.isLoggingOut = false
        state.projectNames = []
      },
      userName(state, action) {
        const name = action.payload
        state.name = name
      },
      userTokenRefreshing(state) {
        state.tokenRefreshing = true
      },
      userToken(state, action) {
        const { bearerToken, tokenExpires } = action.payload
        state.bearerToken = bearerToken
        state.tokenExpires = tokenExpires
      },
      userPolicy(state, action) {
        const policy = action.payload
        state.policy = policy
      },
      userProjectsFetching(state, action) {
        const projectsFetching = action.payload
        state.projectsFetching = projectsFetching
      },
      userProjects(state, action) {
        const projects = action.payload
        state.projects = projects
      },
      userProjectNames(state, action) {
        const projectNames = action.payload
        state.projectNames = projectNames
      },
      userRole(state, action) {
        const role = action.payload
        state.role = role
      },
      userAccountsFetching(state, action) {
        const accountsFetching = action.payload
        state.accountsFetching = accountsFetching
      },
      setAccounts(state, action) {
        const accounts = action.payload
        state.accounts = accounts
      },
      changePinnedStatus(state, action) {
        const projectName = action.payload
        const allProjects = { ...current(state) }.projectNames
        let requiredIndex = 0
        for (let pIndex = 0; pIndex < allProjects.length; pIndex++) {
          if (allProjects[pIndex].u_project_name === projectName) {
            requiredIndex = pIndex
            break
          }
        }
        const clonedProjects = JSON.parse(
          JSON.stringify(current(state).projectNames)
        )
        clonedProjects[requiredIndex].u_project_pinned =
          !clonedProjects[requiredIndex].u_project_pinned
        const currentProject = clonedProjects[requiredIndex]
        clonedProjects.splice(requiredIndex, 1)
        if (currentProject.u_project_pinned) {
          clonedProjects.unshift(currentProject)
        } else {
          clonedProjects.push(currentProject)
        }
        state.projectNames = clonedProjects
      },
      setAllowedPortalComponents(state, action) {
        const allowedPortalComponents = action.payload
        state.allowedPortalComponents = allowedPortalComponents
      },
      toggleClientView(state, action) {
        const clientView = action.payload
        state.clientView = clientView
      }
    },
    extraReducers: {
      [userLogout.fulfilled]: (state, _) => {
        state.isLoggingIn = false
        state.name = null
        state.email = null
        state.projects = null
        state.projectNames = null
        state.isAuthenticated = false
        state.tokenExpires = null
        state.bearerToken = null
        state.policy = null
        state.role = null
      },
      [userLogout.rejected]: (_, _2) => {},
      [userLogin.fulfilled]: (state, action) => {
        state.name = action.payload.name
        state.email = action.payload.email
        state.projects = action.payload.projects
        state.isAuthenticated = true
        state.tokenExpires = action.payload.tokenExpires
        state.bearerToken = action.payload.token
        state.policy = action.payload.policy
        state.role = action.payload.role
      },
      [userLogin.rejected]: (state, action) => {
        state.isLoggingIn = false
        if (action.error) {
          console.error(action.error)
        }
      },
      [refreshToken.fulfilled]: (state, action) => {
        state.bearerToken = action.payload.token
        state.tokenExpires = action.payload.tokenExpires
      },
      [refreshToken.rejected]: (state, action) => {
        if (action.error) {
          console.error(action.error)
        }
      }
    }
  })

const userSlice = createUserSlice()
const actions = userSlice.actions
const reducer = userSlice.reducer

export {
  actions as userActions,
  reducer as userReducer,
  createUserSlice,
  userLogin,
  refreshToken,
  userLogout
}
